/**
 * DATA.REACH reachability slice decision procedure (RD-27 §6).
 *
 * Given a finite authority DAG and a source/destination pair, computes
 * the minimal subgraph containing every path from source to destination
 * and decides reachability. Sound and complete for finite DAGs
 * (ABK-2 slice-sound, ABK-3 slice-complete).
 *
 * Companion to agda/Research/AgenticBoundaryKernel/ReachabilitySlice.agda.
 * React-free and pure: every export is a total function of its inputs.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type {
  AuthorityDag,
  AuthorityEdge,
  NodeId,
  ReachabilitySlice,
} from "./types.js";

export type {
  AuthorityDag,
  AuthorityEdge,
  AuthorityNode,
  NodeId,
  ReachabilitySlice,
} from "./types.js";

function adjacency(
  edges: readonly AuthorityEdge[],
): ReadonlyMap<NodeId, readonly NodeId[]> {
  const out = new Map<NodeId, NodeId[]>();
  for (const e of edges) {
    let bucket = out.get(e.from);
    if (bucket === undefined) {
      bucket = [];
      out.set(e.from, bucket);
    }
    bucket.push(e.to);
  }
  return out;
}

/**
 * Set of nodes reachable from `start` over the given adjacency map,
 * including `start` itself. Iterative BFS, O(V + E).
 */
function reachableFrom(
  start: NodeId,
  adj: ReadonlyMap<NodeId, readonly NodeId[]>,
): Set<NodeId> {
  const seen = new Set<NodeId>([start]);
  const queue: NodeId[] = [start];
  let head = 0;
  while (head < queue.length) {
    const current = queue[head];
    head += 1;
    for (const next of adj.get(current) ?? []) {
      if (seen.has(next)) continue;
      seen.add(next);
      queue.push(next);
    }
  }
  return seen;
}

/**
 * Compute the reachability slice for `(source, destination)` in `dag`.
 *
 * 1. FWRD(s) = forward reachable from source
 * 2. BACK(d) = nodes from which destination is reachable (forward
 *    reachability on the reversed edge set)
 * 3. slice nodes = FWRD(s) ∩ BACK(d)
 * 4. slice edges = edges with both endpoints in the slice
 * 5. reachable iff destination ∈ slice nodes
 *
 * Runs in O(V + E) time and space.
 */
export function reachabilitySlice(
  dag: AuthorityDag,
  source: NodeId,
  destination: NodeId,
): ReachabilitySlice {
  const forwardAdj = adjacency(dag.edges);
  const backwardAdj = adjacency(
    dag.edges.map((e) => ({ from: e.to, to: e.from })),
  );

  const forwardSet = reachableFrom(source, forwardAdj);
  const backwardSet = reachableFrom(destination, backwardAdj);

  const sliceSet = new Set<NodeId>();
  for (const node of forwardSet) {
    if (backwardSet.has(node)) sliceSet.add(node);
  }

  const nodeOrder = new Map<NodeId, number>();
  dag.nodes.forEach((n, index) => nodeOrder.set(n.id, index));
  const ordered = (set: ReadonlySet<NodeId>): NodeId[] =>
    [...set].sort((a, b) => (nodeOrder.get(a) ?? 0) - (nodeOrder.get(b) ?? 0));

  const sliceEdges = dag.edges.filter(
    (e) => sliceSet.has(e.from) && sliceSet.has(e.to),
  );

  return {
    forward: ordered(forwardSet),
    backward: ordered(backwardSet),
    sliceNodes: ordered(sliceSet),
    sliceEdges,
    reachable: sliceSet.has(destination),
  };
}

/**
 * Whether a directed edge lies inside the computed slice. Convenience
 * for rendering: the lab highlights slice edges differently from
 * ambient delegation edges.
 */
export function edgeInSlice(
  slice: ReachabilitySlice,
  edge: AuthorityEdge,
): boolean {
  return slice.sliceEdges.some((e) => e.from === edge.from && e.to === edge.to);
}
